import React from 'react';

/** برچسب فارسی هر سؤال تشریحی، دقیقاً هم‌معنی با متن سؤال در فرم عمومی */
const NARRATIVE_LABELS: Record<string, string> = {
  proudestAchievement: 'به کدام دستاورد بیشتر افتخار می‌کنید؟ (شرایط قبل، اقدام، نتیجه)',
  improvedSomethingAfterJoining: 'چه چیزی بعد از حضور شما واقعاً بهتر شد؟',
  bestUnaskedInitiative: 'بهترین کاری که بدون درخواست کسی انجام دادید؟',
  managerReferenceGuess: 'مدیر قبلی درباره مهم‌ترین ارزش حضورتان چه می‌گوید؟',

  multiPressureScenario: 'چند اولویت هم‌زمان: از کجا شروع می‌کنید و چرا؟',
  lateDeliveryScenario: 'وقتی می‌بینید به موعد نمی‌رسید، چه می‌کنید و کِی اطلاع می‌دهید؟',
  hardestCrisisExperienced: 'سخت‌ترین بحران کاری که تجربه کرده‌اید؟',
  last24hCrisisScenario: '۲۴ ساعت مانده به یک دوره مهم، بحران پیش می‌آید — اولین سه اقدام؟',

  noInstructionTask: 'کاری بدون دستورالعمل کامل — از کجا شروع می‌کنید؟',
  repeatedFailureApproach: 'بعد از چند تلاش ناموفق، کِی روش را عوض یا کمک می‌گیرید؟',

  lastImprovementIdea: 'آخرین باری که چیزی را بهتر کردید — چه شد؟',
  inefficientProcessReaction: 'با فرایند ناکارآمد و «همیشه همینه» چه می‌کنید؟',
  unconvincedManagerIdea: 'ایده دارید ولی مدیر قانع نشده — چه می‌کنید؟',
  behaviorChoice: 'رفتار واقعی نزدیک‌تر به کدام است؟',
  behaviorChoiceReason: 'چرا این گزینه را انتخاب کردید؟',
  failedIdeaNextStep: 'اگر ایده جدید نتیجه ندهد، قدم بعدی چیست؟',

  brokenPromiseExperience: 'قولی که نتوانستید عملی کنید — چه شد و بعدش چه کردید؟',
  ownMistakeExample: 'یک اشتباه کاری خودتان که مشکل ایجاد کرد؟',
  firstCheckWhenResultIsBad: 'وقتی نتیجه بد می‌شود، اول چه چیزی را بررسی می‌کنید؟',

  sevenTasksInAWeek: 'با ۷ کار مهم در یک هفته چطور مطمئن می‌شوید چیزی جا نمی‌ماند؟',
  timeManagementMethods: 'روش مدیریت زمان',
  startTimingReasoning: 'برای کار ۳ روزه با مهلت یک هفته، کِی شروع می‌کنید؟',

  disagreeWithTeamDecision: 'با تصمیم تیم مخالفید و تغییر نکرده — از این لحظه چه می‌کنید؟',
  independentVsDisrespectfulLine: 'مرز «نظر مستقل» و «بی‌احترامی به تصمیم تیم» کجاست؟',
  mockingManagementDecisionReaction: 'همکار تصمیم مدیریت را مسخره می‌کند و شما هم بخشی از نقد را قبول دارید — واکنش؟',
  goodCultureTraits: 'سه ویژگی مهم یک فرهنگ سازمانی خوب',
  unacceptableColleagueBehavior: 'چه رفتاری از همکار قابل قبول نیست؟',

  preferredColleague: 'ترجیح همکاری با کدام فرد؟',
  preferredColleagueReason: 'چرا؟',
  firstReactionToNewProblem: 'اولین واکنش به مسئله جدید',
  mostAnnoyingTrait: 'بیشترین آزاردهنده',
  mostAnnoyingTraitReason: 'چرا؟',

  howHeardAboutUs: 'نحوه آشنایی با مجموعه',
  whyApplyingToUs: 'چرا برای همکاری با ما اقدام کردید؟',
  whatDoYouKnowAboutUs: 'درباره فعالیت مجموعه چه می‌دانید؟',
  valueYouCanBring: 'چه ارزشی می‌توانید ایجاد کنید؟',
  bestEnvironmentForYou: 'بهترین محیط کاری برای شما',
  worstEnvironmentForYou: 'محیط کاری که عملکردتان را بد می‌کند',
  sixMonthSuccessScenario: 'بعد از ۶ ماه، چه اتفاقی باعث می‌شود بگوییم «انتخاب خوبی کردیم»؟',
};

const BEHAVIOR_CHOICE_TEXT: Record<string, string> = {
  A: 'A) اگر چیزی خوب کار می‌کند، بی‌دلیل تغییرش نمی‌دهم.',
  B: 'B) حتی اگر خوب کار کند، کنجکاوم ببینم بهتر هم می‌شود یا نه.',
  C: 'C) اول فایده، هزینه و ریسک تغییر را می‌سنجم.',
};

const PREFERRED_COLLEAGUE_TEXT: Record<string, string> = {
  A: 'فرد A: بسیار خلاق و ایده‌پرداز، اما گاهی غیرقابل‌اتکا در زمان تحویل.',
  B: 'فرد B: بسیار منظم و قابل‌اتکا، اما کم‌ایده‌تر.',
};

const NARRATIVE_SECTIONS: Array<{ title: string; fields: string[] }> = [
  { title: '۷ | دستاورد و اثرگذاری', fields: ['proudestAchievement', 'improvedSomethingAfterJoining', 'bestUnaskedInitiative', 'managerReferenceGuess'] },
  { title: '۸ | بحران، فشار و تصمیم‌گیری', fields: ['multiPressureScenario', 'lateDeliveryScenario', 'hardestCrisisExperienced', 'last24hCrisisScenario'] },
  { title: '۹ | چالش و حل مسئله', fields: ['noInstructionTask', 'repeatedFailureApproach'] },
  { title: '۱۰ | نوآوری و بهبود', fields: ['lastImprovementIdea', 'inefficientProcessReaction', 'unconvincedManagerIdea', 'behaviorChoice', 'behaviorChoiceReason', 'failedIdeaNextStep'] },
  { title: '۱۱ | مسئولیت‌پذیری و قابل‌اتکا بودن', fields: ['brokenPromiseExperience', 'ownMistakeExample', 'firstCheckWhenResultIsBad'] },
  { title: '۱۲ | نظم و مدیریت کار', fields: ['sevenTasksInAWeek', 'timeManagementMethods', 'startTimingReasoning'] },
  { title: '۱۳ | فرهنگ سازمانی و همکاری تیمی', fields: ['disagreeWithTeamDecision', 'independentVsDisrespectfulLine', 'mockingManagementDecisionReaction', 'goodCultureTraits', 'unacceptableColleagueBehavior'] },
  { title: '۱۴ | چند انتخاب کوتاه', fields: ['preferredColleague', 'preferredColleagueReason', 'firstReactionToNewProblem', 'mostAnnoyingTrait', 'mostAnnoyingTraitReason'] },
  { title: '۱۵ | درباره همکاری با ما', fields: ['howHeardAboutUs', 'whyApplyingToUs', 'whatDoYouKnowAboutUs', 'valueYouCanBring', 'bestEnvironmentForYou', 'worstEnvironmentForYou', 'sixMonthSuccessScenario'] },
];

const isEmpty = (v: any) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const formatValue = (field: string, value: any): string => {
  if (isEmpty(value)) return '-';
  if (field === 'behaviorChoice') return BEHAVIOR_CHOICE_TEXT[value] || value;
  if (field === 'preferredColleague') return PREFERRED_COLLEAGUE_TEXT[value] || value;
  if (Array.isArray(value)) return value.join('، ');
  return String(value);
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 p-2.5">
    <p className="text-[11px] font-semibold text-slate-500">{label}</p>
    <p className="mt-0.5 whitespace-pre-wrap text-[12.5px] text-slate-800">{value}</p>
  </div>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 p-3">
    <p className="mb-2 text-xs font-bold text-slate-700">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

/** نمایش خوانای همه‌ی پاسخ‌های شغلی متقاضی (بدون اطلاعات هویتی) — جایگزین دامپ JSON خام */
export const RecruitmentAnswersView: React.FC<{ answers: Record<string, any> }> = ({ answers }) => {
  const workConditions = answers?.workConditions || {};
  const education: any[] = answers?.education || [];
  const skills: any[] = answers?.skills || [];
  const courses: any[] = answers?.courses || [];
  const workExperiences: any[] = answers?.workExperiences || [];
  const narrative = answers?.narrative || {};

  return (
    <div className="space-y-3">
      <SectionCard title="۲ | شرایط همکاری">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Field label="وضعیت فعلی" value={formatValue('', workConditions.currentStatus)} />
          <Field label="زمان امکان شروع همکاری" value={formatValue('', workConditions.availableFrom)} />
          <Field label="نوع همکاری موردنظر" value={formatValue('', workConditions.cooperationType)} />
          <Field label="حقوق / دستمزد مورد انتظار" value={formatValue('', workConditions.expectedSalary)} />
          <Field label="محدودیت روز یا ساعت کاری" value={formatValue('', workConditions.hoursLimitation)} />
        </div>
      </SectionCard>

      {education.length ? (
        <SectionCard title="۳ | تحصیلات و وضعیت خدمت">
          <div className="space-y-2">
            {education.map((row, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2.5 text-[12px] sm:grid-cols-5">
                <p><span className="text-slate-500">مقطع: </span>{row.degree || '-'}</p>
                <p><span className="text-slate-500">رشته: </span>{row.field || '-'}</p>
                <p><span className="text-slate-500">دانشگاه: </span>{row.university || '-'}</p>
                <p><span className="text-slate-500">سال پایان: </span>{row.endYear || '-'}</p>
                <p><span className="text-slate-500">وضعیت: </span>{row.status || '-'}</p>
              </div>
            ))}
          </div>
          <Field label="وضعیت خدمت نظام وظیفه" value={formatValue('', answers?.militaryServiceStatus)} />
        </SectionCard>
      ) : null}

      {skills.length || answers?.otherSkills ? (
        <SectionCard title="۴ | مهارت‌های تخصصی و نرم‌افزارها">
          {skills.map((row, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2.5 text-[12px] sm:grid-cols-4">
              <p><span className="text-slate-500">ابزار: </span>{row.tool || '-'}</p>
              <p><span className="text-slate-500">سطح: </span>{row.level || '-'}</p>
              <p><span className="text-slate-500">مدت تجربه: </span>{row.experienceDuration || '-'}</p>
              <p><span className="text-slate-500">آخرین استفاده: </span>{row.lastUsed || '-'}</p>
            </div>
          ))}
          {answers?.otherSkills ? <Field label="سایر مهارت‌ها" value={answers.otherSkills} /> : null}
        </SectionCard>
      ) : null}

      {courses.length ? (
        <SectionCard title="۵ | دوره‌ها، کارگاه‌ها و سمینارها">
          {courses.map((row, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2.5 text-[12px] sm:grid-cols-5">
              <p><span className="text-slate-500">عنوان: </span>{row.title || '-'}</p>
              <p><span className="text-slate-500">برگزارکننده: </span>{row.organizer || '-'}</p>
              <p><span className="text-slate-500">سال: </span>{row.year || '-'}</p>
              <p><span className="text-slate-500">مدت: </span>{row.duration || '-'}</p>
              <p><span className="text-slate-500">کاربرد: </span>{row.usage || '-'}</p>
            </div>
          ))}
        </SectionCard>
      ) : null}

      {workExperiences.length ? (
        <SectionCard title="۶ | سوابق و تجارب شغلی">
          {workExperiences.map((exp, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-2.5">
              <p className="mb-1.5 text-[11.5px] font-bold text-slate-700">
                تجربه شغلی {(i + 1).toLocaleString('fa-IR')}
              </p>
              <div className="grid grid-cols-1 gap-2 text-[12px] sm:grid-cols-2">
                <p><span className="text-slate-500">شرکت: </span>{exp.companyName || '-'}</p>
                <p><span className="text-slate-500">زمینه فعالیت: </span>{exp.field || '-'}</p>
                <p><span className="text-slate-500">سمت: </span>{exp.position || '-'}</p>
                <p><span className="text-slate-500">مدت: </span>{exp.durationFrom || '-'} تا {exp.durationTo || '-'}</p>
                <p className="sm:col-span-2"><span className="text-slate-500">مدیر مستقیم: </span>{exp.managerNameOrTitle || '-'}</p>
              </div>
              {exp.mainResponsibilities ? <Field label="مهم‌ترین مسئولیت‌ها" value={exp.mainResponsibilities} /> : null}
              {exp.endReason ? <Field label="دلیل پایان همکاری" value={exp.endReason} /> : null}
            </div>
          ))}
        </SectionCard>
      ) : null}

      {NARRATIVE_SECTIONS.map((section) => {
        const visibleFields = section.fields.filter((f) => !isEmpty(narrative[f]));
        if (!visibleFields.length) return null;
        return (
          <SectionCard key={section.title} title={section.title}>
            {visibleFields.map((f) => (
              <Field key={f} label={NARRATIVE_LABELS[f] || f} value={formatValue(f, narrative[f])} />
            ))}
          </SectionCard>
        );
      })}
    </div>
  );
};

export default RecruitmentAnswersView;
